import * as d3 from 'd3';
import React, {useCallback} from 'react';

import {Sequence} from 'components/Sequence';
import * as context from 'Context';
import {ORFLayout} from 'molbio/maps/circular/CircularLayout';
import {
  DISPLAY_START_POSITION_FLAG_THRESHOLD,
  getHiddenBackgroundPath,
  getLinePath,
  getStartPositionFlagPath,
  getStartPositionTickPath,
} from 'molbio/maps/circular/CircularORFUtil';
import {MinimalMouseEventContext} from 'molbio/maps/circular/CircularTypedefs';
import ORF from 'molbio/translations/backbone/ORF';
import {getAngles} from 'util/geometry/GeometryUtil';

interface CircularORFProps {
  /**
   * A function mapping from base pair index to the angle in radians on the unit circle at which ORFs at that
   * base pair should be drawn on screen.
   */
  basePairToAngle: d3.ScaleLinear<number, number>;
  /** A function to map from the coordinates of the cursor to the corresponding base pair */
  getHoverBase: (e: Event, round: boolean) => number;
  /** The cid of the CircularMap mapstate */
  mapstateCid: string;
  /** A function to be called when the ORF is double-clicked */
  onDoubleClick: () => void;
  /** A function to be called when the ORF is clicked */
  onMouseDown: (e: MinimalMouseEventContext) => void;
  /** A function to be called when a click on the ORF is released */
  onMouseUp: (e: MinimalMouseEventContext) => void;
  /** The Backbone ORFLayout instance for this circular map */
  layout: ORFLayout;
  /** The Backbone ORF objects to render. */
  orf: ORF;
  /**
   * A scaling function. Accepts a count of ring heights away from the outermost ring (and towards the
   * center), and returns the amount of pixels that ring should be away from the center.
   */
  radiusIn: d3.ScaleLinear<number, number>;
  /** The sequence being rendered */
  sequence: Sequence;
}

/**
 * React component to render an SVG representing a single ORF.
 */
function CircularORF({
  basePairToAngle,
  getHoverBase,
  layout,
  mapstateCid,
  onDoubleClick,
  onMouseDown,
  onMouseUp,
  orf,
  radiusIn,
  sequence,
}: CircularORFProps): JSX.Element {
  const sequenceLength = sequence.length();
  const angles = getAngles(orf.start(), orf.end(), sequenceLength, basePairToAngle);
  const color = orf.get('hover') || orf.selected() ? d3.rgb(orf.color()).darker().formatHex() : orf.color();
  const layer = orf.get('annularLayer');
  const vMargin = layout.vMargin();

  // We want the start position indicator to be the width of a codon. If a codon would be less than
  // DISPLAY_START_POSITION_FLAG_THRESHOLD pixels wide when rendered at the current zoom, display a thin
  // vertical tick at each start position instead of a flag.
  const startPositionRenderedLengthBP = 2 * Math.PI * (3 / sequenceLength) * layout.get('innerRadius');
  const startPositionLongerThanCodon = startPositionRenderedLengthBP > DISPLAY_START_POSITION_FLAG_THRESHOLD;

  const onMouseOver = useCallback(
    (e: React.MouseEvent<SVGPathElement>) => {
      e.preventDefault();
      orf.set({hover: true});
    },
    [orf]
  );
  const onMouseLeave = useCallback(
    (e: React.MouseEvent<SVGPathElement>) => {
      e.preventDefault();
      orf.set({hover: false});
    },
    [orf]
  );
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<SVGPathElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onMouseDown(e satisfies MinimalMouseEventContext);
      const index = getHoverBase(e as unknown as Event, false);
      context.using({isLinear: false, mapId: mapstateCid, index}, orf, () => {
        orf.click(sequence, false, index);
      });
    },
    [getHoverBase, onMouseDown, orf, sequence, mapstateCid]
  );
  const handleMouseUp = useCallback(
    (e: React.MouseEvent<SVGPathElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onMouseUp(e satisfies MinimalMouseEventContext);
    },
    [onMouseUp]
  );
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent<SVGPathElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onDoubleClick();
      const index = getHoverBase(e as unknown as Event, false);
      context.using({isLinear: false, mapId: mapstateCid, index}, orf, () => {
        orf.dblClick(sequence, false, index);
      });
    },
    [getHoverBase, onDoubleClick, orf, sequence, mapstateCid]
  );

  return (
    <g className="individual-ring" id={orf.cid}>
      <g className="ring-shape">
        <path
          className="line orf"
          d={getLinePath({angles, layer, radiusIn, vMargin})}
          fill="none"
          shapeRendering="geometricPrecision"
          stroke={color}
          strokeWidth={1}
          onDoubleClick={handleDoubleClick}
          onMouseDown={handleMouseDown}
          onMouseLeave={onMouseLeave}
          onMouseOver={onMouseOver}
          onMouseUp={handleMouseUp}
        />
        <path
          d={getHiddenBackgroundPath({angles, layer, orf, radiusIn})}
          fillOpacity={0}
          onDoubleClick={handleDoubleClick}
          onMouseDown={handleMouseDown}
          onMouseLeave={onMouseLeave}
          onMouseOver={onMouseOver}
          onMouseUp={handleMouseUp}
        />
        {orf.startPositions().map((startPosition, index) => (
          <path
            className="start-position layer-fill"
            d={
              startPositionLongerThanCodon
                ? getStartPositionFlagPath({
                    basePairToAngle,
                    index,
                    layer,
                    orf,
                    radiusIn,
                    sequenceLength,
                    vMargin,
                  })
                : getStartPositionTickPath({
                    basePairToAngle,
                    index,
                    layer,
                    orf,
                    radiusIn,
                    sequenceLength,
                    vMargin,
                  })
            }
            key={`orf-${orf.cid}-start-position-${startPosition}`}
            fill={color}
            fillOpacity={1}
            onDoubleClick={handleDoubleClick}
            onMouseDown={handleMouseDown}
            onMouseLeave={onMouseLeave}
            onMouseOver={onMouseOver}
            onMouseUp={handleMouseUp}
            shapeRendering="geometricPrecision"
            stroke="green"
            strokeWidth={1}
          />
        ))}
      </g>
    </g>
  );
}

interface CircularORFsProps {
  /**
   * A function mapping from base pair index to the angle in radians on the unit circle at which ORFs at that
   * base pair should be drawn on screen.
   */
  basePairToAngle: d3.ScaleLinear<number, number>;
  /** A list of Backbone ORF objects to render. */
  data: Array<ORF>;
  /** A function to map from the coordinates of the cursor to the corresponding base pair */
  getHoverBase: (e: Event, round: boolean) => number;
  /** The Backbone ORFLayout instance for this circular map */
  layout: ORFLayout;
  /** The cid of the CircularMap mapstate */
  mapstateCid: string;
  /** A function to be called when the ORF is double-clicked */
  onDoubleClick: () => void;
  /** A function to be called when the ORF is clicked */
  onMouseDown: (e: MinimalMouseEventContext) => void;
  /** A function to be called when a click on the ORF is released */
  onMouseUp: (e: MinimalMouseEventContext) => void;
  /**
   * A scaling function. Accepts a count of ring heights away from the outermost ring (and towards the
   * center), and returns the amount of pixels that ring should be away from the center.
   */
  radiusIn: d3.ScaleLinear<number, number>;
  /** The Backbone sequence being rendered */
  sequence: Sequence;
}

export default function CircularORFs({
  basePairToAngle,
  data,
  getHoverBase,
  layout,
  mapstateCid,
  onDoubleClick,
  onMouseDown,
  onMouseUp,
  radiusIn,
  sequence,
}: CircularORFsProps): JSX.Element {
  return (
    <>
      {data.map((orf) => (
        <CircularORF
          basePairToAngle={basePairToAngle}
          getHoverBase={getHoverBase}
          key={`orf-${orf.cid}`}
          layout={layout}
          mapstateCid={mapstateCid}
          onDoubleClick={onDoubleClick}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          orf={orf}
          radiusIn={radiusIn}
          sequence={sequence}
        />
      ))}
    </>
  );
}
